/**
 * Every customer-facing string the Workers surfaces print, in one file
 * (YAG-371).
 *
 * It lives apart from the components for one reason: the copy rules are
 * test-pinned, and a test can only pin strings it can enumerate. `copy.test.ts`
 * walks everything exported here.
 *
 * The rules (scoping addendum section 1, binding):
 *   - the category noun is **Agent**. Never persona, worker, employee, hire,
 *     container, Mission, Commitment, or tier.
 *   - Agents are **they/them**.
 *   - no em-dashes. The separator is a middot, as in the prototype.
 *   - no version numbers, anywhere.
 */

import type {
  CodeReviewReportStatus,
  GithubPrStatus,
  ProctorPrStatus,
  RefinementDropReason,
  ReviewFinding,
} from "@yagni/shared";

/** The Chip tones this surface uses; matches `ui/chip.tsx` variants. */
export type WorkerChipTone = "neutral" | "accent" | "success" | "honesty" | "incident" | "dashed";

/* ── the sentence editor (spec §2) ──────────────────────────────────────── */

export const RHYTHM_EDITOR_TITLE = "When it starts";
export const RHYTHM_EDITOR_MODE_TIMES = "at fixed times";
export const RHYTHM_EDITOR_MODE_EVERY = "every so often";
export const RHYTHM_EDITOR_TIMES_LABEL = "Times of day";
export const RHYTHM_EDITOR_TIME_ADD = "add a time";
export const RHYTHM_EDITOR_TIME_REMOVE = "remove";
export const RHYTHM_EDITOR_EVERY_LABEL = "How often";
export const RHYTHM_EDITOR_FROM_LABEL = "From";
export const RHYTHM_EDITOR_TO_LABEL = "To";
export const RHYTHM_EDITOR_DAYS_LABEL = "Days";
export const RHYTHM_EDITOR_PRESET_WEEKDAYS = "Weekdays";
export const RHYTHM_EDITOR_PRESET_EVERY_DAY = "Every day";
export const RHYTHM_EDITOR_TZ_LABEL = "Timezone";
export const RHYTHM_EDITOR_SAVE = "Save this rhythm";
export const RHYTHM_EDITOR_SAVED = "Saved.";
/** A legacy row the sentence editor cannot show: say so, and start clean. */
export const RHYTHM_EDITOR_LEGACY_NOTE =
  "This rhythm still runs on the Team's publication times. The times below are " +
  "the craft's own starting point, and saving replaces the old shape with them.";

/** The interval choices, as the select says them. */
export const RHYTHM_INTERVAL_LABEL: Record<number, string> = {
  30: "every 30 minutes",
  60: "every hour",
  120: "every 2 hours",
  180: "every 3 hours",
  240: "every 4 hours",
  360: "every 6 hours",
  480: "every 8 hours",
};

/**
 * A refused save, in words. The keys are the server's own refusal codes
 * (`validateSchedule`, plus the two the engagement route adds), so a code this
 * build has no sentence for still says something true rather than leaking the
 * code onto the page.
 */
export const RHYTHM_ERROR_COPY: Record<string, string> = {
  times_too_close: "Two times have to sit at least 30 minutes apart.",
  schedule_conflict: "A rhythm runs at fixed times or on an interval, never both.",
  every_minutes_invalid: "Pick one of the intervals offered.",
  between_invalid: "The window has to end after it starts.",
  times_invalid: "Every time has to read as a clock time, such as 9:00.",
  days_invalid: "Pick the days from the week above.",
  tz_invalid: "That timezone is not one the clock can read.",
  schedule_empty: "Pick at least one time before saving.",
  no_rhythm_row: "This Team has no rhythm for them yet, so there is nothing to schedule.",
  reactive_wake_unsupported: "This Agent has no wake to switch.",
  invalid_rhythm: "Could not read that schedule. Check the times and try again.",
};
export const RHYTHM_ERROR_FALLBACK = "Could not save this rhythm. Check the times and try again.";

/** The refusal, whatever the server called it. */
export function rhythmErrorLine(code: string | null): string | null {
  if (!code) return null;
  return RHYTHM_ERROR_COPY[code] ?? RHYTHM_ERROR_FALLBACK;
}

/** Provenance chips. The whole point of the pane is that these never lie. */
export const PROVENANCE_CHIP = {
  platform: "inherited · platform",
  workspace: "inherited · workspace",
  team: "set here",
} as const;

export const RESET_LABEL = "Reset to inherited";

/** "Jul 12" / "Jul 12, 2025" once it is not this year. */
export function shortDate(iso: string, now: Date = new Date()): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  const sameYear = at.getUTCFullYear() === now.getUTCFullYear();
  return at.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  });
}

/** The modal header's pass chip, from the newest report's prepare output. */
export const REVIEW_PASS_CHIP = {
  initial: "initial review",
  followUp: "follow-up review",
} as const;

/** Where the PR stands on GitHub. Lowercase chips, material carries meaning. */
export const GH_STATUS_CHIP: Record<GithubPrStatus, string> = {
  open: "open",
  draft: "draft",
  merged: "merged",
  closed: "closed",
};
export const GH_STATUS_TONE: Record<GithubPrStatus, WorkerChipTone> = {
  open: "success",
  draft: "dashed",
  merged: "accent",
  closed: "neutral",
};

/** What the worker is doing about the PR. `held for you` = a real open gate. */
export const PROCTOR_STATUS_CHIP: Record<ProctorPrStatus, string> = {
  waiting: "waiting",
  queued: "queued",
  reviewing: "reviewing",
  reviewed: "reviewed",
  held_for_you: "held for you",
  needs_follow_up: "needs follow-up",
  failed: "failed",
};
export const PROCTOR_STATUS_TONE: Record<ProctorPrStatus, WorkerChipTone> = {
  waiting: "dashed",
  // Re-armed for another pass: accent like reviewing, but no pulse — the
  // run has not started yet, and the pulse is the live-run signal alone.
  queued: "accent",
  reviewing: "accent",
  reviewed: "success",
  held_for_you: "honesty",
  needs_follow_up: "honesty",
  failed: "incident",
};

/** One review report's status chip in the modal. One meaning per word. */
export const REPORT_STATUS_CHIP: Record<CodeReviewReportStatus, string> = {
  in_progress: "in progress",
  posted: "posted",
  held: "held for you",
  parked: "draft parked",
  premise_stale: "skipped · the threads changed",
  skipped_stale: "skipped · the head moved",
  discarded: "discarded",
  superseded: "superseded by a newer push",
  cancelled: "cancelled",
  failed: "failed",
};
export const REPORT_STATUS_TONE: Record<CodeReviewReportStatus, WorkerChipTone> = {
  in_progress: "accent",
  posted: "success",
  held: "honesty",
  parked: "honesty",
  premise_stale: "neutral",
  skipped_stale: "neutral",
  discarded: "neutral",
  superseded: "neutral",
  cancelled: "neutral",
  failed: "incident",
};

/** Why a check did not run. Keys are the pipeline's stable skip reasons. */
export const SKIP_REASON_COPY: Record<string, string> = {
  disabled_by_team: "switched off for this Team",
  inactive_for_paths: "no changed files in their area",
  below_min_profile: "the change is too small for this check",
  trivial: "the change is too small for this check",
  trivial_no_signals: "trivial change · no auth or external surface",
  no_grounding: "no recorded grounding to check against",
  unknown_activation: "activation not understood · skipped safely",
  self_skip: "they declined to run it",
  no_delta: "nothing changed in their area since the last review",
  dedicated_stage: "covered by the deep pass",
};

/**
 * The dropped mark. Carried findings wear no label at all: carriage is the
 * norm, and the mark only exists to explain a strikethrough (YAG-417).
 *
 * With a model-origin decision (YAG-423) the mark carries the editor's own
 * drop reason — it IS a verdict. Without one (heuristic or legacy rows) the
 * bare string keeps the softer reconstruction framing.
 */
export const FINDING_NOT_CARRIED = "not carried into the review";

/** The editor's drop reasons, in the reader's words.
 *
 * Typed against the shared union so a new drop reason without copy here is
 * a compile error, not a raw enum leaking into the receipt. The runtime
 * lookup stays Record<string, string> — unknown keys (legacy rows) degrade
 * to the bare reason string in notCarriedLine.
 */
const REFINEMENT_DROP_REASON_COPY_EXHAUSTIVE: Record<RefinementDropReason, string> = {
  already_raised: "already raised on this pull request",
  missed_window: "had its moment on the first review",
  speculative: "judged speculative",
  out_of_scope: "out of scope for this change",
  unfair: "judged unfair",
  contradicted_by_diff: "contradicted by the diff",
  other: "dropped by the editor",
};
export const REFINEMENT_DROP_REASON_COPY: Record<string, string> =
  REFINEMENT_DROP_REASON_COPY_EXHAUSTIVE;

/** The full dropped-row mark: reason when the editor gave one, legacy string otherwise. */
export function notCarriedLine(decision: {
  reason?: string | null;
  note?: string | null;
  disposition?: string | null;
  source?: string | null;
}): string {
  // duplicate_source (YAG-435): someone else already raised it — say who.
  if (decision.disposition === "duplicate_source") {
    const who = decision.source ? `@${decision.source}` : "another reviewer";
    const base = `${FINDING_NOT_CARRIED} · already raised by ${who}`;
    return decision.note ? `${base} · ${decision.note}` : base;
  }
  const reason = decision.reason ? REFINEMENT_DROP_REASON_COPY[decision.reason] : null;
  const base = reason ? `${FINDING_NOT_CARRIED} · ${reason}` : FINDING_NOT_CARRIED;
  return decision.note ? `${base} · ${decision.note}` : base;
}

/**
 * Re-exported from the `Finding` primitive (YAG-424) so this file stays the
 * one place the surface's strings are enumerated, while the implementation
 * lives with the component that renders them. Two copies of a path truncator
 * is how the two renderings drifted in the first place.
 */
export { truncatedPathLabel } from "../ui/index.js";

export const REVIEWS_LOADING = "Reading the reviews.";
export const REVIEWS_ERROR = "Could not read the reviews just now. Reload and they will be here.";

export const RERUN_LABEL = "Re-run review";
// True on both paths: the route kicks a scoped sweep directly, and the
// scheduler tick is the durable fallback. Never "queued for the next sweep",
// which reads as a delay when the claim lands in a second.
export const RERUN_QUEUED = "re-run started";
/**
 * A merged or closed pull request is off the watch, so the re-arm behind
 * "Re-run review" refuses it (staging 2026-09-17, NEARM-396). The path that
 * works is the Team's own review request, which reads the pull request as it
 * stands and opens as its own case (a terminal case stays terminal). The verb
 * says which of the two it is, and the note says where the review lands.
 */
export const REVIEW_AGAIN_LABEL = "Review again";
export const REVIEW_AGAIN_EFFECT =
  "This pull request is no longer open, so its watch is over. This asks Proctor for one more read of it as it stands, which opens as its own case.";
export const REVIEW_AGAIN_QUEUED =
  "a fresh review of the closed pull request is queued · it opens as its own case";
export const REVIEW_AGAIN_QUEUED_MERGED =
  "a fresh review of the merged pull request is queued · it opens as its own case";

/** One run's history line: when it started, then where it ended up. */
export function runHistoryLine(report: {
  startedAt: string;
  status: CodeReviewReportStatus;
}): string {
  const at = new Date(report.startedAt);
  if (Number.isNaN(at.getTime())) return REPORT_STATUS_CHIP[report.status];
  const hh = String(at.getUTCHours()).padStart(2, "0");
  const mm = String(at.getUTCMinutes()).padStart(2, "0");
  return `${shortDate(report.startedAt)} ${hh}:${mm} · ${REPORT_STATUS_CHIP[report.status]}`;
}

/** Roster states for stages that have not written a record yet. */
export const ROSTER_RUNNING = "reviewing now";
export const ROSTER_WAITING = "waiting";
/** A terminal run's unreached stages say so; a checkmark nobody earned lies. */
export const ROSTER_NOT_REACHED = "did not run";
export const ROSTER_NO_FINDINGS = "no findings";

/** The stage error record's machine code → the roster's prose (YAG-490). */
const STAGE_ERROR_COPY: Record<string, string> = {
  dispatch_error: "provider error",
  token_cap: "output token cap",
  parse_error: "unparseable output",
};

/**
 * A FAILED stage's roster note (YAG-490): what happened, from the persisted
 * error record. Stage outputs written before error records existed carry no
 * code — those keep an honest generic line.
 */
export function stageFailedNote(errorCode?: string, errorDetail?: string): string {
  const reason = errorCode?.startsWith("provider_error:")
    ? `provider error ${errorCode.slice("provider_error:".length)}`
    : (STAGE_ERROR_COPY[errorCode ?? ""] ?? errorCode);
  const head = `did not run — ${reason ?? "the check errored"}`;
  return errorDetail ? `${head} · ${errorDetail}` : head;
}

/** Tier-failover substitution suffix (YAG-490): the record line for a stage
 * that succeeded on a different tier. Fact only — the incident story lives in
 * logs/Sentry, not on the pane. */
export function ranViaSuffix(toTier: string): string {
  return ` · ran via ${toTier}`;
}

/** A check whose pass never read the code (its loop read nothing and its
 * single-shot fallback failed). It did not fail, and it did not review. */
export function unreadSuffix(): string {
  return " · did not read the code";
}

/** YAG-551: chunked retry suffix — the stage ran on chunked input after a
 * context-overflow 400, not the full diff. Fact only. */
export function chunkedRetrySuffix(chunks: number): string {
  return ` · ran via chunked retry (${chunks} chunk${chunks === 1 ? "" : "s"})`;
}

/** YAG-551: refinement degradation suffix — the stage ran on a reduced input. */
export function refinementDegradedSuffix(
  level: "capped_candidates" | "diff_windows" | "no_diff",
): string {
  const labels: Record<string, string> = {
    capped_candidates: "capped candidates",
    diff_windows: "excerpted diff",
    no_diff: "no diff",
  };
  return ` · ran with ${labels[level] ?? level}`;
}

/** The refinement row's note: what got carried, from recorded counts only. */
export function refinementRosterNote(decisions: {
  survivors: ReviewFinding[];
  merged: number | null;
  dropped: number | null;
}): string {
  const parts = [`${decisions.survivors.length} carried`];
  const counts = modelCountsLine(decisions.merged, decisions.dropped);
  if (counts) parts.push(counts);
  return parts.join(" · ");
}

/** "16 findings · 1 decision · 3 risk" over the roster; null when none. */
export function summaryFindingsLine(survivors: ReviewFinding[]): string | null {
  if (survivors.length === 0) return null;
  const rollup = severityRollupLine(survivors);
  const count = findingsCountLabel(survivors.length);
  return rollup ? `${count} · ${rollup}` : count;
}

/** The detail pane of a specialist whose record carries nothing. */
export const DETAIL_NO_FINDINGS = "no findings from this specialist";

/**
 * "1 of 7 carried" — a specialist's findings against the refinement cut
 * (YAG-417). Only printed once decisions exist; sums can slightly exceed the
 * survivor count when refinement merges two kept findings into one.
 */
export function carriedCountLine(carried: number, found: number): string {
  return `${carried} of ${found} carried`;
}

export { SEVERITY_CHIP_TONE, severityChipTone } from "../ui/index.js";

/**
 * The reviewer's own call (YAG-417): the refinement verdict, made before any
 * autonomy hold or approval floor. THE readiness signal for autonomy: what
 * they would decide, regardless of what training let them deliver.
 */
export const VERDICT_CALL_CHIP: Record<"approve" | "comment_only", string> = {
  approve: "would approve",
  comment_only: "would comment only",
};
export const VERDICT_CALL_TONE: Record<"approve" | "comment_only", WorkerChipTone> = {
  approve: "success",
  comment_only: "neutral",
};
export const VERDICT_CALL_EXPLAINER =
  "their call, before the review level, any hold or approval floor";
/** The upgraded variant (YAG-441): verification refuted every carried finding. */
export const VERDICT_CALL_EXPLAINER_UPGRADED =
  "recomputed after verification refuted every carried finding, before the review level, any hold or approval floor";

/**
 * What GitHub received from the review: the card leads with it. The model's
 * call above is a detail beside it, because GitHub may have received
 * something else (an approval floor held it, or the pull request is the
 * bot's own and GitHub lets no author approve it).
 */
export const GITHUB_EVENT_CHIP: Record<"APPROVE" | "COMMENT", string> = {
  APPROVE: "approved on GitHub",
  COMMENT: "commented on GitHub",
};
export const GITHUB_EVENT_TONE: Record<"APPROVE" | "COMMENT", WorkerChipTone> = {
  APPROVE: "success",
  COMMENT: "neutral",
};

/** The persisted composed review's section title and inline-comment label. */
export const REVIEW_PREVIEW_TITLE = "the review as composed";
export const REVIEW_PREVIEW_INLINE_TITLE = "inline comments";

/**
 * The fixed stage names; `role:<slug>` prints its slug. The whole-diff pass
 * says "whole diff", never "deep review": a Playbook role named Deep review
 * exists, and two rows wearing one name read as a duplicate (YAG-417).
 */
export const STAGE_LABELS: Record<string, string> = {
  prepare: "prepare",
  deep: "whole diff",
  diff_check: "follow-up check",
  refinement: "refinement",
  verification: "verification",
};

/**
 * Per-finding verification marks (YAG-419). Distinct from the editor's
 * "not carried" strike: these findings SURVIVED the editor and were then
 * checked against the actual checkout.
 */
export const VERIFICATION_MARK_COPY: Record<string, string> = {
  refuted: "refuted in verification",
  unverified: "could not be verified · lowered to a nit",
};

/** The verification roster note: "3 confirmed · 1 refuted · 1 unverified". */
export function verificationRosterNote(counts: {
  confirmed?: number;
  refuted?: number;
  unverified?: number;
}): string {
  const parts = [
    `${counts.confirmed ?? 0} confirmed`,
    ...((counts.refuted ?? 0) > 0 ? [`${counts.refuted} refuted`] : []),
    ...((counts.unverified ?? 0) > 0 ? [`${counts.unverified} unverified`] : []),
  ];
  return parts.join(" · ");
}

/** Appended to the Summary findings line when verification refuted survivors. */
export function refutedCountLine(refuted: number): string {
  return `${refuted} refuted in verification`;
}

/** The verification stage errored: honesty note, findings passed through unchecked. */
export const VERIFICATION_FAILED_NOTE = "did not complete · findings passed through unchecked";

export function stageLabel(key: string): string {
  return STAGE_LABELS[key] ?? (key.startsWith("role:") ? key.slice(5) : key);
}

/** "3 findings" / "1 finding" appended to a stage header that carries any. */
export function findingsCountLabel(count: number): string {
  return `${count} finding${count === 1 ? "" : "s"}`;
}

/**
 * "2 risk · 3 nit" from the refinement survivors. Severity is the producer's
 * free text, grouped verbatim (never re-vocabularied), most frequent first;
 * unlabeled findings are not counted, and no labeled survivors prints
 * nothing at all.
 */
export function severityRollupLine(survivors: ReviewFinding[]): string | null {
  const counts = new Map<string, number>();
  for (const finding of survivors) {
    if (finding.severity) counts.set(finding.severity, (counts.get(finding.severity) ?? 0) + 1);
  }
  if (counts.size === 0) return null;
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([severity, count]) => `${count} ${severity}`)
    .join(" · ");
}

/**
 * "2 merged · 1 dropped" from the refinement's own recorded counts. A count
 * nobody recorded is not said; both absent prints nothing at all.
 */
export function modelCountsLine(merged: number | null, dropped: number | null): string | null {
  const parts: string[] = [];
  if (merged !== null) parts.push(`${merged} merged`);
  if (dropped !== null) parts.push(`${dropped} dropped`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/** "4m 12s" between two report timestamps; null when either is unusable. */
export function durationLabel(startedAt: string, completedAt: string | null): string | null {
  if (!completedAt) return null;
  const start = new Date(startedAt).getTime();
  const end = new Date(completedAt).getTime();
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return null;
  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/* ── the follow-up outcome (design pass, §6) ── */

/**
 * What happened to a prior comment after the author pushed (YAG-357's
 * diff_check). Computed and stored since July, rendered nowhere until now:
 * `diff_check` emits `{entries}` rather than `{findings}`, so the modal's
 * stage reader found no findings array and printed "no findings from this
 * specialist" over the best trust signal in the system.
 *
 * Honest by construction upstream: `resolved` and `partially_addressed` are
 * only permitted when the anchored file actually changed in the delta, and
 * `answered` only when there is a real author reply. `persists` is the one
 * that must not be softened.
 */
export const FOLLOW_UP_CLASSIFICATION_COPY: Record<string, string> = {
  resolved: "addressed before merge",
  partially_addressed: "partly addressed",
  answered: "the author answered it",
  persists: "still there",
  treated_addressed: "treated as addressed",
};

/** Honesty suffix flags for a prior-comment row's mark. */
export interface DiffCheckMarkFlags {
  /** The pinned-prior pre-filter emitted this entry — it was never re-judged. */
  pinned?: boolean;
  /** A persists/partially_addressed with no live current-code evidence. */
  evidenceReady?: boolean;
}

export function followUpMark(
  classification: string,
  note: string | null,
  flags?: DiffCheckMarkFlags,
): string {
  const word = FOLLOW_UP_CLASSIFICATION_COPY[classification] ?? classification;
  const parts = [word];
  if (flags?.pinned === true) parts.push("held resolved from an earlier round");
  if (flags?.evidenceReady === false) parts.push("unproven, no current-code evidence");
  if (note) parts.push(note);
  return parts.join(" · ");
}

/** "20 raised · 14 addressed · 1 answered · 5 still there" — counted, never
 * claimed. `pinnedCount` appends the held-resolved suffix when the
 * pre-filter emitted entries deterministically, so a reader can tell pinned
 * bookkeeping from fresh judgment. Callers pass classification counts that
 * EXCLUDE pinned entries (countClassifications skips them); the raised
 * total adds them back so the buckets still cover every entry. */
export function followUpOutcomeLine(
  counts: Record<string, number>,
  pinnedCount = 0,
): string | null {
  const total = Object.values(counts).reduce((sum, n) => sum + n, 0) + pinnedCount;
  if (total === 0) return null;
  const order = ["resolved", "partially_addressed", "answered", "persists", "treated_addressed"];
  const parts = [`${total} raised`];
  for (const key of order) {
    const n = counts[key] ?? 0;
    if (n > 0) parts.push(`${n} ${FOLLOW_UP_CLASSIFICATION_COPY[key] ?? key}`);
  }
  if (pinnedCount > 0) parts.push(`${pinnedCount} held resolved`);
  return parts.join(" · ");
}

export const HARPER_RUN_DELIVERY_FAILED_BAND = "This Update never reached its channel";
export const HARPER_RUN_DELIVERY_RETRY = "Retry";
export const HARPER_RUN_NO_DELIVERY_NOTE = "No channel set; this job's Updates live only in Yagni.";
export const HARPER_RUN_STAGES_TITLE = "Stages";
export const HARPER_RUN_FILING_TITLE = "The Update";
export const HARPER_RUN_RETRY_STARTED = "Retry started";
export const HARPER_RUN_RETRY_FAILED = "Could not start the retry just now.";
